import { useState } from "react";
import { X, AlertTriangle, Send } from "lucide-react";
import { useToolAccess } from "@/hooks/useToolAccess";
import { useToast } from "@/hooks/use-toast";
import { analytics } from "@/hooks/useAnalytics";

const ISSUE_TYPES = [
  { value: "bug", label: "Something isn't working" },
  { value: "accuracy", label: "Inaccurate results" },
  { value: "slow", label: "Tool is slow or unresponsive" },
  { value: "ui", label: "Display or layout issue" },
  { value: "suggestion", label: "Feature suggestion" },
  { value: "other", label: "Other" },
];

interface IssueReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  toolId?: string;
  toolName?: string;
}

export const IssueReportModal = ({ isOpen, onClose, toolId, toolName }: IssueReportModalProps) => {
  const { submitIssue, email, fullName } = useToolAccess();
  const { toast } = useToast();
  
  const [issueType, setIssueType] = useState("");
  const [description, setDescription] = useState("");
  const [userEmail, setUserEmail] = useState(email || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!issueType || !description.trim()) {
      toast({
        title: "Missing information",
        description: "Please select an issue type and describe the problem",
        variant: "destructive",
      });
      return;
    }

    if (!userEmail.trim()) {
      toast({
        title: "Email required",
        description: "Please enter your email so we can follow up",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await submitIssue(issueType, description, toolId, toolName);
      analytics.issueReported(toolName || "unknown", issueType);
      toast({
        title: "Issue reported",
        description: "Thank you! We'll look into this and may follow up via email.",
      });
      onClose();
      setIssueType("");
      setDescription("");
    } catch (error: any) {
      toast({
        title: "Failed to submit",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />
      
      <div 
        className="relative w-full max-w-md bg-card rounded-2xl border border-border shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Report an Issue</h3>
              {toolName && (
                <p className="text-sm text-muted-foreground">{toolName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Issue Type
            </label>
            <select
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">Select issue type...</option>
              {ISSUE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Your Email
            </label>
            <input
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe what happened, what you expected, and any steps to reproduce..."
              rows={4}
              className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Submit Report
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};